/**
 * TimesheetProvider holds the timesheet list state for the timesheet screens.
 *
 * It handles:
 * - fetching timesheets filtered by task and date
 * - exposing loading and error state to the UI
 */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { TimesheetRepository } from '../data/timesheetRepository';
import { useAuth } from '../../auth/logic/authProvider';

const TimesheetContext = createContext({
  timesheets: [],
  listTimesheets: async () => [],
  createTimesheet: async () => false,
  updateTimesheet: async () => false,
  deleteTimesheet: async () => false,
  clearError: () => {},
  isLoading: false,
  error: null,
});

export const TimesheetProvider = ({ children }) => {
  const { user } = useAuth();
  const [timesheets, setTimesheets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const inFlight = useRef(null);
  const latest = useRef(0);

  /**
   * Fetch timesheets for the given filters and store them.
   *
   * Returns the fetched list, or an empty list when the request fails.
   *
   * A call repeating the filters of a running one joins it instead of starting
   * a second: StrictMode double-invokes effects in dev, and the backend answers
   * 502 when both hit it at once. A call with different filters does start its
   * own request, and only the newest one is allowed to write the list — the
   * filters change on every dropdown pick, so responses can land out of order.
   */
  const listTimesheets = useCallback(
    async (filters = {}) => {
      const { projectId, taskId, dateFrom, dateTo } = filters;
      const key = JSON.stringify([projectId, taskId, dateFrom, dateTo]);
      if (inFlight.current?.key === key) return inFlight.current.promise;

      const seq = (latest.current += 1);

      setIsLoading(true);
      setError(null);

      const request = (async () => {
        try {
          const result = await TimesheetRepository.listTimesheets({
            projectId,
            taskId,
            dateFrom,
            dateTo,
            accessToken: user?.accessToken,
          });
          if (seq === latest.current) setTimesheets(result.timesheets);
          return result.timesheets;
        } catch (err) {
          if (seq === latest.current) {
            setError(err);
            setTimesheets([]);
          }
          return [];
        } finally {
          if (inFlight.current?.key === key) inFlight.current = null;
          if (seq === latest.current) setIsLoading(false);
        }
      })();

      inFlight.current = { key, promise: request };
      return request;
    },
    [user?.accessToken],
  );

  /**
   * Create a line and prepend it to the list. Returns true on success.
   *
   * The created line is prepended rather than refetched: the list is sorted
   * id-desc, so the new line belongs at the top either way.
   */
  const createTimesheet = useCallback(
    async ({ taskId, date, timeSpent, description } = {}) => {
      setIsLoading(true);
      setError(null);
      try {
        const created = await TimesheetRepository.createTimesheet({
          taskId,
          date,
          timeSpent,
          description,
          accessToken: user?.accessToken,
        });
        setTimesheets((current) => [created, ...current]);
        return true;
      } catch (err) {
        setError(err);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [user?.accessToken],
  );

  /**
   * Update a line in place. Returns true on success.
   */
  const updateTimesheet = useCallback(
    async ({ id, timeSpent, description } = {}) => {
      setIsLoading(true);
      setError(null);
      try {
        const updated = await TimesheetRepository.updateTimesheet({
          id,
          timeSpent,
          description,
          accessToken: user?.accessToken,
        });
        setTimesheets((current) => current.map((item) => (item.id === updated.id ? updated : item)));
        return true;
      } catch (err) {
        setError(err);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [user?.accessToken],
  );

  /**
   * Delete a line and drop it from the list. Returns true on success.
   */
  const deleteTimesheet = useCallback(
    async (id) => {
      setIsLoading(true);
      setError(null);
      try {
        await TimesheetRepository.deleteTimesheet({ id, accessToken: user?.accessToken });
        setTimesheets((current) => current.filter((item) => item.id !== id));
        return true;
      } catch (err) {
        setError(err);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [user?.accessToken],
  );

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo(
    () => ({
      timesheets,
      listTimesheets,
      createTimesheet,
      updateTimesheet,
      deleteTimesheet,
      clearError,
      isLoading,
      error,
    }),
    [timesheets, listTimesheets, createTimesheet, updateTimesheet, deleteTimesheet, clearError, isLoading, error],
  );

  return <TimesheetContext.Provider value={value}>{children}</TimesheetContext.Provider>;
};

export const useTimesheets = () => useContext(TimesheetContext);
